import React from 'react';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';

import CheckoutSectionHeading from './checkout/CheckoutSectionHeading';
import { v2Colors } from '../design/theme';

/**
 * Pilot-only slot fields for summary / orders (no Prana fields).
 * @param {Record<string, unknown>} slot
 */
export function slotToSummaryPayload(slot) {
  if (!slot || typeof slot !== 'object') return null;
  return {
    id: slot.id,
    displayTime: slot.displayTime,
    startTime: slot.startTime,
    endTime: slot.endTime,
    active: slot.active !== false,
    availability: slot.availability ?? null,
  };
}

function DayToggle({ label, selected, onClick }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        flex: 1,
        py: 1.25,
        px: 2,
        borderRadius: 2,
        border: '1px solid',
        borderColor: selected ? v2Colors.primary : v2Colors.border,
        bgcolor: selected ? v2Colors.primary : v2Colors.surface,
        color: selected ? v2Colors.canvas : v2Colors.primary,
        fontWeight: 600,
        fontSize: '0.9rem',
        fontFamily: 'inherit',
        cursor: 'pointer',
        transition: 'background-color 0.15s, color 0.15s',
        '&:hover': {
          bgcolor: selected ? v2Colors.primaryDark : v2Colors.tagFill,
          borderColor: v2Colors.primary,
        },
      }}
    >
      {label}
    </Box>
  );
}

function SlotButton({ label, selected, disabled, onClick }) {
  return (
    <Box
      component="button"
      type="button"
      role="option"
      aria-selected={selected}
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
      sx={{
        flex: '1 1 calc(50% - 6px)',
        maxWidth: 'calc(50% - 6px)',
        minWidth: 0,
        py: 1.25,
        px: 1,
        borderRadius: 2,
        border: '1px solid',
        borderStyle: disabled ? 'dashed' : 'solid',
        borderColor: selected && !disabled ? v2Colors.primary : v2Colors.border,
        bgcolor: disabled ? v2Colors.tagFill : selected ? v2Colors.primary : v2Colors.surface,
        color: disabled ? v2Colors.slotDisabled : selected ? v2Colors.canvas : v2Colors.heading,
        fontWeight: 600,
        fontSize: '0.8rem',
        lineHeight: 1.25,
        fontFamily: 'inherit',
        cursor: disabled ? 'not-allowed' : 'pointer',
        textDecoration: disabled ? 'line-through' : 'none',
        textAlign: 'center',
        transition: 'border-color 0.15s, background-color 0.15s',
        '&:hover': disabled
          ? {}
          : {
              borderColor: v2Colors.primary,
              bgcolor: selected ? v2Colors.primaryDark : v2Colors.tagFill,
            },
      }}
    >
      {label}
    </Box>
  );
}

export default function CheckoutSlots({
  storeId,
  deliveryDay,
  onDeliveryDayChange,
  slots,
  slotsLoading,
  slotsError,
  selectedSlotId,
  onSelectSlotId,
}) {
  const allSlotsDisabled =
    slots.length > 0 &&
    slots.every((s) => Boolean(s.isDisabled) || s.active === false);

  if (!storeId) {
    return (
      <Alert severity="info" sx={{ mb: 0, borderRadius: 2 }}>
        Select a delivery address to see available time slots.
      </Alert>
    );
  }

  return (
    <Box>
      <CheckoutSectionHeading>Choose your slot</CheckoutSectionHeading>

      <Box sx={{ display: 'flex', gap: 1.5, mb: 2.5 }}>
        <DayToggle
          label="Today"
          selected={deliveryDay === 'today'}
          onClick={() => onDeliveryDayChange('today')}
        />
        <DayToggle
          label="Tomorrow"
          selected={deliveryDay === 'tomorrow'}
          onClick={() => onDeliveryDayChange('tomorrow')}
        />
      </Box>

      {slotsError ? (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {slotsError}
        </Alert>
      ) : null}

      {slotsLoading ? (
        <Box display="flex" justifyContent="center" py={2}>
          <CircularProgress size={28} sx={{ color: v2Colors.primary }} />
        </Box>
      ) : slots.length === 0 || allSlotsDisabled ? (
        <Typography variant="body2" sx={{ color: v2Colors.primaryMuted }}>
          No slots available for this day.
        </Typography>
      ) : (
        <Box
          role="listbox"
          aria-label="Delivery time slots"
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1.25,
          }}
        >
          {slots.map((slot) => {
            const id = String(slot.id);
            const disabled = Boolean(slot.isDisabled) || slot.active === false;
            const selected = String(selectedSlotId || '') === id;
            const label = slot.displayTime || id;
            return (
              <SlotButton
                key={id}
                label={label}
                selected={selected}
                disabled={disabled}
                onClick={() => onSelectSlotId(id)}
              />
            );
          })}
        </Box>
      )}
    </Box>
  );
}
